import React, { createContext, useContext } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useAuth } from './auth'
import {
  effectivePermissions, rank,
  type AppRole, type PermissionLevel,
} from '@wsfull/shared'

type PermissionsContextValue = {
  levelFor: (screenKey: string) => PermissionLevel
  can: (screenKey: string, need?: PermissionLevel) => boolean
  isLoading: boolean
}

const PermissionsContext = createContext<PermissionsContextValue | null>(null)

export function PermissionsProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth()
  const role = (user?.role ?? '') as AppRole

  // Fallback local: defaults do próprio perfil (nunca deixa o menu vazio se a API falhar).
  const fallback = React.useMemo(() => (role ? effectivePermissions(role) : {}), [role])

  const { data, isLoading } = useQuery<Record<string, PermissionLevel>>({
    queryKey: ['permissions', 'me', role],
    queryFn: async () => (await api.get('/permissions/me')).data.permissions,
    enabled: isAuthenticated && !!role,
    staleTime: 5 * 60 * 1000,
  })

  const map = data ?? fallback
  const levelFor = (screenKey: string): PermissionLevel =>
    role === 'admin' ? 'edit' : map[screenKey] ?? 'none'
  const can = (screenKey: string, need: PermissionLevel = 'read'): boolean =>
    rank[levelFor(screenKey)] >= rank[need]

  return (
    <PermissionsContext.Provider value={{ levelFor, can, isLoading: isLoading && !data }}>
      {children}
    </PermissionsContext.Provider>
  )
}

export function usePermissions() {
  const ctx = useContext(PermissionsContext)
  if (!ctx) throw new Error('usePermissions must be used within PermissionsProvider')
  return ctx
}
